import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Crown, Loader2, XCircle } from "lucide-react";
import { Particles } from "@/components/catalog/Particles";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { api } from "@/lib/api";
import { money } from "@/pages/Pricing";

const Shell = ({ children, testId }) => (
  <div data-testid={testId} className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-4 pt-28 pb-16">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.16),transparent_60%)]" />
    <Particles count={16} />
    <div className="relative z-10 w-full max-w-lg panel p-8 sm:p-10 text-center">{children}</div>
  </div>
);

export function BillingSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useBilling();
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ phase: "checking", billing: null });

  useEffect(() => {
    const id = params.get("session_id");
    if (!id) return setState({ phase: "error" });
    let tries = 0;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${id}`);
        if (data.payment_status === "paid") {
          const b = await refresh();
          await checkAuth();
          if (alive) setState({ phase: "paid", billing: b, amount: data.amount, currency: data.currency });
          return;
        }
        if (data.status === "expired") return alive && setState({ phase: "expired" });
      } catch { /* retry */ }
      if (++tries >= 20) return alive && setState({ phase: "timeout" });
      setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [params, refresh, checkAuth]);

  const ent = state.billing?.entitlements;
  return (
    <Shell testId="billing-success-page">
      {state.phase === "checking" && <><Loader2 className="mx-auto h-12 w-12 animate-spin text-[#D4AF37]" /><h1 className="mt-5 font-display text-2xl gold-text">Confirming your payment…</h1><p className="mt-2 text-sm text-[#8B8B8B]">Verifying with Stripe. This takes a few seconds.</p></>}
      {state.phase === "paid" && (
        <>
          <CheckCircle2 className="mx-auto h-14 w-14 text-[#FFD86A]" strokeWidth={1.4} />
          <p className="eyebrow mt-5 flex items-center justify-center gap-2"><Crown className="h-4 w-4" /> Plan activated</p>
          <h1 data-testid="billing-success-plan" className="mt-2 font-display text-4xl font-bold gold-text">{ent?.plan_name}</h1>
          <p className="mt-3 text-sm text-white/70">The full AETRYX catalog is unlocked{ent?.ai_credits_total ? <> and <span data-testid="billing-success-credits" className="text-[#7CF7FF]">{ent.ai_credits_total} AI credits</span> are ready for Create Studio</> : ""}.</p>
          {state.amount != null && <p data-testid="billing-success-amount" className="mt-4 text-xs text-[#8B8B8B]">Charged {money(state.amount, state.currency)} today · includes applicable taxes</p>}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" data-testid="billing-success-watch-btn" className="btn-gold px-6 py-3 text-sm">Start watching</Link>
            <Link to="/account#billing" data-testid="billing-success-account-btn" className="btn-ghost-gold px-6 py-3 text-sm">View billing</Link>
          </div>
        </>
      )}
      {["timeout", "expired", "error"].includes(state.phase) && (
        <>
          <XCircle className="mx-auto h-12 w-12 text-[#ff6b6b]" />
          <h1 data-testid="billing-success-error" className="mt-5 font-display text-2xl text-white">{state.phase === "expired" ? "Checkout expired" : "We couldn't confirm the payment yet"}</h1>
          <p className="mt-2 text-sm text-[#8B8B8B]">If you were charged, your plan will appear in Account → Billing shortly.</p>
          <Link to="/pricing" className="btn-ghost-gold mt-7 inline-flex px-6 py-3 text-sm">Back to pricing</Link>
        </>
      )}
    </Shell>
  );
}

export function BillingCancel() {
  return (
    <Shell testId="billing-cancel-page">
      <XCircle className="mx-auto h-12 w-12 text-[#D4AF37]" strokeWidth={1.4} />
      <h1 className="mt-5 font-display text-3xl gold-text">Checkout canceled</h1>
      <p className="mt-3 text-sm text-[#8B8B8B]">No charge was made. You can pick a plan whenever you're ready.</p>
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/pricing" data-testid="billing-cancel-pricing-btn" className="btn-gold px-6 py-3 text-sm">View plans</Link>
        <Link to="/" className="btn-ghost-gold px-6 py-3 text-sm">Keep watching</Link>
      </div>
    </Shell>
  );
}
